/**
 * Fase 2 (W11): tipos do que a API dos assistentes devolve e a ponte com a passkey do navegador (WebAuthn). A biometria
 * (Face ID, Touch ID, digital) fica no aparelho: o navegador só devolve a assinatura do desafio, que o servidor confere.
 */

export type EstadoAssistente = 'ativo' | 'suspenso' | 'revogado';
export type EstadoAprovacao = 'pendente' | 'aprovada' | 'recusada' | 'expirada' | 'falhou';

export interface Assistente {
  id: string;
  nome: string;
  estado: EstadoAssistente;
  suspenso_motivo: string | null;
  suspenso_em: string | null;
  niveis: Record<string, number>;
  acoes_por_dia: number | null;
  valor_maximo_centavos: number | string | null;
  horario_inicio: string | null;
  horario_fim: string | null;
  dias_semana: number[] | null;
  anomalia_exclusoes: number;
  anomalia_janela_min: number;
  prazo_aprovacao_horas: number;
  criado_em: string;
  prefixo: string | null;
  escopos: string[] | null;
  ultimo_uso: string | null;
  pendentes: number;
  acoes_hoje: number;
}

export interface OpcoesAssistente {
  modulos: { slug: string; nome: string; reservado: boolean; pode_escrever: boolean }[];
  permissoes: { codigo: string; modulo: string; nivel_padrao: number; nivel_papel: number; travado: boolean }[];
  maximo: number;
}

export interface ResumoAprovacao {
  registro_id: string | null;
  valor_centavos: number | null;
  exclusao: boolean;
  acao: string;
}

export interface Aprovacao {
  id: string;
  estado: EstadoAprovacao;
  nivel: number;
  origem: 'rota' | 'mcp';
  alvo: string;
  modulo: string;
  permissao: string | null;
  resumo: ResumoAprovacao;
  assistente_id: string;
  assistente_nome: string;
  criada_em: string;
  expira_em: string;
  decidida_em: string | null;
  prova_metodo: 'passkey' | 'totp' | null;
  resultado: { ok?: boolean; valor?: unknown; erro?: { codigo: string; mensagem: string }; codigo?: string } | null;
  entrada?: unknown;
  assistente_estado?: EstadoAssistente;
  /** Review r16 visual B10: o registro que a ação toca, com o nome (só no detalhe; nulo quando não há ou não foi achado). */
  registro?: { entidade: string; rotulo: string } | null;
}

export interface UsoAssistente {
  periodo: { de: string; ate: string };
  usuario_id: string;
  acionamentos: number;
  feitas: number;
  preparadas: number;
  aprovadas: number;
  recusadas: number;
  expiradas: number;
  falharam: number;
  pendentes: number;
  alteracoes: { via_assistente: number; direto: number; percentual_assistente: number | null };
  tempo_medio_confirmacao_s: number | null;
  modulos: { modulo: string; acoes: number }[];
  pessoas: { id: string; nome: string }[];
}

export interface ItemLixeira {
  entidade: string;
  id: string;
  rotulo: string | null;
  /** Review r17 spec (B3): nome do cliente citado no rótulo (oportunidade, projeto), para o modo discreto. */
  parceiro_nome?: string | null;
  excluido_em: string;
  restaurar_ate: string;
  assistente_nome: string | null;
  pode_restaurar: boolean;
}

export interface PasskeyDaConta {
  id: string;
  nome: string;
  criada_em: string;
  ultimo_uso_em: string | null;
}

/** Nível de risco N0 a N3 e a cor do selo (verde direto, laranja confirma, perigo reservado). */
export const COR_NIVEL = ['neutro', 'verde', 'laranja', 'perigo'] as const;

// ------------------------------------------------------------------------------------------------------------ WebAuthn
const paraBuffer = (b64u: string): ArrayBuffer => {
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
  return u.buffer;
};
const paraB64u = (b: ArrayBuffer): string => {
  const u = new Uint8Array(b);
  let s = '';
  for (let i = 0; i < u.length; i++) s += String.fromCharCode(u[i]!);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** O navegador sabe criar e usar passkey? */
export function suportaPasskey(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function' && !!navigator.credentials;
}

export interface OpcoesCadastroPasskey {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: 'public-key'; alg: number }[];
  timeout: number;
  attestation: AttestationConveyancePreference;
  authenticatorSelection: AuthenticatorSelectionCriteria;
  excludeCredentials: { type: 'public-key'; id: string; transports?: string[] }[];
}

/** `navigator.credentials.create` com as opções do servidor; devolve o que o servidor confere. */
export async function criarPasskey(o: OpcoesCadastroPasskey) {
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: paraBuffer(o.challenge),
      rp: o.rp,
      user: { id: paraBuffer(o.user.id), name: o.user.name, displayName: o.user.displayName },
      pubKeyCredParams: o.pubKeyCredParams,
      timeout: o.timeout,
      attestation: o.attestation,
      authenticatorSelection: o.authenticatorSelection,
      excludeCredentials: o.excludeCredentials.map((c) => ({ type: 'public-key' as const, id: paraBuffer(c.id), transports: c.transports as AuthenticatorTransport[] | undefined })),
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('passkey.cancelada');
  const r = cred.response as AuthenticatorAttestationResponse;
  const transportes = typeof r.getTransports === 'function' ? r.getTransports() : [];
  return { client_data_json: paraB64u(r.clientDataJSON), attestation_object: paraB64u(r.attestationObject), transportes };
}

export interface DesafioAprovacao {
  challenge: string | null;
  rpId: string;
  allowCredentials: { type: 'public-key'; id: string; transports?: string[] }[];
  userVerification: UserVerificationRequirement;
  timeout: number;
  totp_disponivel: boolean;
}

/** `navigator.credentials.get` com o desafio da aprovação (a biometria do aparelho confirma). */
export async function assinarComPasskey(d: DesafioAprovacao & { challenge: string }) {
  const cred = (await navigator.credentials.get({
    publicKey: {
      challenge: paraBuffer(d.challenge),
      rpId: d.rpId,
      allowCredentials: d.allowCredentials.map((c) => ({ type: 'public-key' as const, id: paraBuffer(c.id), transports: c.transports as AuthenticatorTransport[] | undefined })),
      userVerification: d.userVerification,
      timeout: d.timeout,
    },
  })) as PublicKeyCredential | null;
  if (!cred) throw new Error('passkey.cancelada');
  const r = cred.response as AuthenticatorAssertionResponse;
  return { credencial_id: paraB64u(cred.rawId), client_data_json: paraB64u(r.clientDataJSON), authenticator_data: paraB64u(r.authenticatorData), assinatura: paraB64u(r.signature) };
}

/** Erro do navegador ao pedir a passkey (cancelou, tempo esgotado, sem passkey neste aparelho). */
export const passkeyCancelada = (e: unknown) => e instanceof DOMException || (e instanceof Error && e.message === 'passkey.cancelada');
